/**
 * A Food Code citation in the place's own code (fix/one-truth; the ontology pass's move #6, "a binder in the inspector's own
 * code"). The binder's rule index, the web and phone Prove pages and the state pages print each rule's citation through here.
 *
 * Before: outside Utah each row read "Food Code 2-301.12 …; New York: 10 NYCRR Subpart 14-1 (State Sanitary Code), section not
 * matched yet" (CA 16 rows, NY 18 on the walk), and New York's holding row said 41°F where its law is 45°F.
 *
 * Now, for every FDA section a row cites:
 *  - mapped (states/<xx>-fcindex.ts, research with a verbatim quote per section; the place's own authority first: NYC Health
 *    Code Article 81, SNHD, Northern Nevada Public Health): the state's section is cited, and where its text requires something
 *    else, the row says so ("Here the law differs: …");
 *  - read and absent from the state's code: "FDA Food Code (model) 3-501.17: not in New York's code, so this line is our advice";
 *  - not read far enough: "FDA Food Code (model) 3-402.11, state section not mapped yet: confirm with <who inspects you>".
 * Temperatures use the law-versus-advice resolver (jurisdiction.ts, the same values placelimits.ts judges records by): "The law
 * here: cold food 45°F or below … We recommend 41°F (FDA Food Code 3-501.16(A)(2))."
 *
 * Utah adopts the 2022 Food Code by reference (R392-100-4(1)(b)): its citations come back unchanged.
 */
import { countyIn, packFor, stateName, stateOrDefault } from "./states/index.ts";
import { perStateFrom } from "./states/access.ts";
import type { FcIndexData, FcIndexEntry, UsState } from "./states/types.ts";
import { authorityFor } from "./ruleshapes.ts";
import { resolveRule } from "./ruleshapes.ts";
import { setRuleAt } from "./requirements.ts";
import { foodCodeIndex, type FoodCodeData, type FoodCodeItem, type FoodCodeRow } from "./foodcodeindex.ts";
import type { IsoDate, PermitType } from "./model.ts";
import { lessonsFor, type Lesson } from "./learn.ts";

const UT_INDEX: FcIndexData = { excerpts: "", state: {}, authorities: {}, absent: {}, unmapped: {}, confirmByHand: [] };
/** Every state's index research: Utah's is empty (it adopts the Food Code itself); another state's comes with its bundle. */
export const FC_INDEX: Record<UsState, FcIndexData> = perStateFrom<FcIndexData, Record<string, FcIndexData>>("fcindex", (m, st) => m[`FC_INDEX_${st}`]!, UT_INDEX);

export interface PlaceRef { state?: string | null; county?: string | null }

const FDA_SEC = /\b\d-\d{3}\.\d+/g;
const uniq = <T,>(xs: T[]) => [...new Set(xs)];

/** True where the place's citations are the Food Code's own (Utah: adopted by reference, no index of its own). */
export const citesFoodCodeItself = (place: PlaceRef): boolean => {
  const st = stateOrDefault(place.state);
  return !!packFor(st).foodCode.adoptsByReference && !Object.keys(FC_INDEX[st].state).length;
};

function authorityIdAt(place: PlaceRef): string | undefined {
  const st = stateOrDefault(place.state);
  const a = authorityFor(st, place.county);
  return a.kind === "confirmed" ? a.department.id : undefined;
}

/** Who to confirm with at this place: its health authority, or the state program where the county's isn't confirmed. */
export function confirmWith(place: PlaceRef): string {
  const st = stateOrDefault(place.state);
  const a = authorityFor(st, place.county);
  if (a.kind === "confirmed") return a.department.name;
  if (a.kind === "unconfirmed") return a.stateProgram.name;
  return `your local health department (${packFor(st).stateAgency.name} can tell you which)`;
}

/** The place's index entry for one FDA section: its authority's first, then the state's. */
export function fcEntryAt(fda: string, place: PlaceRef): FcIndexEntry | undefined {
  const idx = FC_INDEX[stateOrDefault(place.state)];
  const auth = authorityIdAt(place);
  return (auth ? idx.authorities[auth]?.[fda] : undefined) ?? idx.state[fda];
}

export interface CodeAt {
  /** The citation to print. Utah: the row's own citation, unchanged. */
  line: string;
  /** The place's own sections, in the row's order. */
  own: string[];
  /** Where the place's text requires something else than the row's FDA text: the section and what, in plain words. */
  differs: { fda: string; section: string; note: string }[];
  /** FDA sections read and not found in the place's code (the row's line is our advice there). */
  absent: string[];
  /** FDA sections not mapped yet (confirm with the authority). */
  unmapped: string[];
}

/** A row's citation at a place. `skipDiffer`: FDA sections whose difference another line already states (the temperature lines). */
export function codeAt(citation: string, place: PlaceRef, o: { skipDiffer?: readonly string[] } = {}): CodeAt {
  if (citesFoodCodeItself(place)) return { line: citation, own: [], differs: [], absent: [], unmapped: [] };
  const st = stateOrDefault(place.state);
  const idx = FC_INDEX[st];
  const secs = uniq(citation.match(FDA_SEC) ?? []);
  const own: string[] = [], differs: CodeAt["differs"] = [], absent: string[] = [], unmapped: string[] = [];
  for (const s of secs) {
    const e = fcEntryAt(s, place);
    if (e) {
      // One section once, the most precise form kept ("10 NYCRR 14-1.40(a)" over "10 NYCRR 14-1.40").
      const i = own.findIndex((x) => x === e.section || x.startsWith(e.section) || e.section.startsWith(x));
      if (i < 0) own.push(e.section);
      else if (e.section.length > own[i]!.length) own[i] = e.section;
      if (!e.same && e.note && !o.skipDiffer?.includes(s)) differs.push({ fda: s, section: e.section, note: e.note });
    } else if (idx.absent[s]) absent.push(s);
    else unmapped.push(s);
  }
  // What the citation names besides Food Code sections (a research note, OSHA 29 CFR 1910.1200, FALCPA) stays.
  const extras = citation.split(/;\s*/).map((x) => x.trim()).filter((x) => x && !/\b\d-\d{3}\.\d+/.test(x) && !/^Food Code\b/.test(x));
  const name = stateName(st);
  const parts = [
    own.length ? `${name}: ${own.join(", ")}` : "",
    absent.length ? `FDA Food Code (model) ${absent.join(", ")}: not in ${name}'s code, so this line is our advice` : "",
    unmapped.length ? `FDA Food Code (model) ${unmapped.join(", ")}, state section not mapped yet: confirm with ${confirmWith(place)}` : "",
    ...extras,
  ].filter(Boolean);
  return { line: parts.join("; ") || citation, own, differs, absent, unmapped };
}

/** "Here the law differs: …" for a row's differing sections, or "". */
export const differsLine = (c: CodeAt): string =>
  c.differs.length ? `Here the law differs: ${c.differs.map((d) => `${d.note.replace(/\.$/, "")} (${d.section})`).join("; ")}.` : "";

// ── Temperatures: the law here, and our advice where the FDA model is stricter (jurisdiction.ts values; placelimits.ts) ──

const MODEL = { cold: 41, hot: 135, cool: [{ fromF: 135, toF: 70, hours: 2 }, { fromF: 70, toF: 41, hours: 4 }] };
type Stage = { fromF: number; toF: number; hours: number };
const isLaw = (s: string) => s === "law" || s === "adopted";
const placeOf = (place: PlaceRef) => { const st = stateOrDefault(place.state); return { state: st, county: countyIn(st, place.county) ?? "" }; };

/** Holding (3-501.16) where the place's law differs from the model's 41°F / 135°F; null where it is the same. */
export function holdingLawAt(place: PlaceRef): { law: string; advice: string } | null {
  if (citesFoodCodeItself(place)) return null;
  const at = placeOf(place);
  const cold = resolveRule<{ maxF: number }>("cold_holding.max_temp", at), hot = resolveRule<{ minF: number }>("hot_holding.min_temp", at);
  const c = isLaw(cold.status) ? cold.value?.maxF : undefined, h = isLaw(hot.status) ? hot.value?.minF : undefined;
  if ((c === undefined || c === MODEL.cold) && (h === undefined || h === MODEL.hot)) return null;
  const cites = uniq([cold, hot].filter((r) => isLaw(r.status)).map((r) => r.rule!.source!.citation));
  const law = `The law here: cold food ${c ?? MODEL.cold}°F or below, hot food ${h ?? MODEL.hot}°F or above (${cites.join("; ")}).`;
  const advice = [c !== undefined && c > MODEL.cold ? `We recommend ${MODEL.cold}°F or below for cold food (FDA Food Code 3-501.16(A)(2)).` : "", h !== undefined && h < MODEL.hot ? `We recommend ${MODEL.hot}°F or above for hot food (FDA Food Code 3-501.16(A)(1)).` : ""].filter(Boolean).join(" ");
  return { law, advice };
}

/** Cooling (3-501.14) where the place's law differs from the model; null where it is the same. */
export function coolingLawAt(place: PlaceRef): { law: string; advice: string } | null {
  if (citesFoodCodeItself(place)) return null;
  const r = resolveRule<{ stages: Stage[]; alternative?: { text: string; citation: string } }>("cooling.stages", placeOf(place));
  const s = isLaw(r.status) ? r.value?.stages : undefined;
  const alt = isLaw(r.status) ? r.value?.alternative : undefined;
  if (!s || (JSON.stringify(s) === JSON.stringify(MODEL.cool) && !alt)) return null;
  const [a, b] = s;
  // A second allowed method (Oregon's shallow-pan cooling, 3-501.14(B)) is law beside the times, cited on its own.
  const law = `The law here: cool from ${a!.fromF}°F to ${a!.toF}°F within ${a!.hours} hours${b ? `, then to ${b.toF}°F within ${b.hours} more hours` : ""} (${r.rule!.source!.citation})${alt ? `, ${alt.text} (${alt.citation})` : ""}.`;
  const end = (b ?? a)!.toF;
  const advice = end > 41 ? "We recommend cooling to 41°F, within 6 hours in all (FDA Food Code 3-501.14(A))." : "";
  return { law, advice };
}

/**
 * A row's summary and citation at the place: the law line first where the temperatures differ, then the row's own words
 * labeled as the FDA model where anything differs, and the differing sections. Unchanged in Utah.
 */
export function ruleAt(row: { summary: string; citation: string }, place: PlaceRef): { summary: string; citation: string; code: CodeAt } {
  if (citesFoodCodeItself(place)) return { summary: row.summary, citation: row.citation, code: { line: row.citation, own: [], differs: [], absent: [], unmapped: [] } };
  const secs: string[] = row.citation.match(FDA_SEC) ?? [];
  const hold = secs.includes("3-501.16") ? holdingLawAt(place) : null;
  const cool = secs.includes("3-501.14") ? coolingLawAt(place) : null;
  const code = codeAt(row.citation, place, { skipDiffer: [...(hold ? ["3-501.16"] : []), ...(cool ? ["3-501.14"] : [])] });
  const lead = [hold, cool].filter((t): t is { law: string; advice: string } => !!t).map((t) => [t.law, t.advice].filter(Boolean).join(" ")).join(" ");
  const differ = differsLine(code);
  // Any part that isn't this place's own law (a differing section, an absent or unmapped one) labels the row's words as the model.
  const labelModel = !!lead || !!differ || code.absent.length > 0 || code.unmapped.length > 0;
  const body = labelModel ? `${lead ? `${lead} ` : ""}The FDA Food Code (model) says: ${row.summary}${differ ? ` ${differ}` : ""}` : row.summary;
  return { summary: body, citation: code.line, code };
}

setRuleAt((row, place) => ruleAt(row, place));

// ── The Food Code index (foodcodeindex.ts) at a place: the PDF binder, the web and phone Prove pages ─────────────────────

/** One index entry in the place's own code: its section as the code an inspector names, its law and our advice, its citation. */
export function foodCodeItemAt(item: FoodCodeItem, place: PlaceRef): FoodCodeItem {
  if (citesFoodCodeItself(place)) return item;
  const r = ruleAt({ summary: item.plain, citation: item.cite }, place);
  const first = r.code.own[0];
  // A state with its own numbering (NAC 446, CalCode, 10 NYCRR) is named by its section; one that adopts the Food Code keeps the number.
  const code = first && !/Food Code/.test(first) ? first : item.code;
  return { ...item, code, plain: r.summary, cite: r.citation };
}

/** foodCodeIndex for a place: the same rows and counts, each entry in the place's own code. */
export function foodCodeIndexAt(permit: PermitType, d: FoodCodeData, range: { from: IsoDate; to: IsoDate }, place: PlaceRef): FoodCodeRow[] {
  return foodCodeIndex(permit, d, range, stateOrDefault(place.state)).map((r) => ({ ...r, item: foodCodeItemAt(r.item, place) }));
}

/** The index's name on the binder and the Prove pages: "Food Code index" where the Food Code is the law (Utah); the place's code elsewhere. */
export const ruleIndexTitle = (place: PlaceRef): string => (citesFoodCodeItself(place) ? "Food Code index" : `Rule index: ${stateName(stateOrDefault(place.state))}'s code`);

/**
 * The lessons at a place: where its law's temperatures differ from the FDA model's, the temperature lesson opens with the law
 * here (the same lines as the binder) and says the lesson's numbers are the model's (fix/one-truth; New York's lesson taught
 * 41°F / 135°F as the rule where its law is 45°F / 140°F). Unchanged where the law is the model's (Utah, California …).
 */
export function lessonsAt(place: PlaceRef): Lesson[] {
  const ls = lessonsFor(stateOrDefault(place.state));
  const lines = [holdingLawAt(place), coolingLawAt(place)].filter((t): t is { law: string; advice: string } => !!t).map((t) => [t.law, t.advice].filter(Boolean).join(" "));
  if (!lines.length) return ls;
  return ls.map((l) => (l.id === "temps" ? { ...l, points: [...lines, "The numbers below are the FDA Food Code's (the model). Where the law here asks for more, the law wins.", ...l.points] } : l));
}
